// Turning pasted recipe text into the canonical recipe shape.
//
// This one is genuinely real, not a mock: it is deterministic string work over
// text the user already handed us, so there is nothing to call out to. A real
// integration might replace it with something better at messy input, but
// nothing here is standing in for a service that doesn't exist.
//
// The governing rule is that it never invents. If an amount isn't in the text,
// the ingredient is flagged rather than guessed; if the servings or the total
// time aren't stated, they stay null and the screen says so. A confident wrong
// number is worse than an admitted gap, because the user can't tell it's wrong
// until they're standing in the shop.

import { looksLikeQuantityLine } from './classifyInput.js';
import { isQuantityMissing } from './recipeFlags.js';

const INGREDIENTS_HEADING = /^\s*(ingredients?|you(?:'| wi)?ll need|shopping list)\s*:?\s*$/i;
const STEPS_HEADING = /^\s*(instructions?|directions?|method|steps?|preparation)\s*:?\s*$/i;
const ANY_HEADING = /^\s*(ingredients?|instructions?|directions?|method|steps?|preparation|you(?:'| wi)?ll need|shopping list|for the .+)\s*:?\s*$/i;

const BULLET = /^\s*[-*•‣◦⁃]\s*/;
const STEP_NUMBER = /^\s*\d+[.)]\s+/;

// "2 cups flour" -> quantity "2 cups", name "flour".
// "Flour - 2 cups" -> quantity "2 cups", name "Flour".
// "Flour" -> quantity null, and the caller flags it.
const LEADING_AMOUNT =
  /^((?:\d+[\d.,/\s]*|[¼-¾⅐-⅞]|\b(?:a|an|one|two|three|four|five|six|seven|eight|nine|ten|half)\b)\s*(?:[¼-¾⅐-⅞]\s*)?(?:cups?|tbsps?|tablespoons?|tsps?|teaspoons?|oz|ounces?|lbs?|pounds?|g|grams?|kg|ml|l|litres?|liters?|pinch(?:es)?|dashes|dash|cloves?|slices?|cans?|handfuls?|bunch(?:es)?|sprigs?|sticks?|packs?|packets?|tins?)?)\s+(.+)$/i;
const TRAILING_AMOUNT = /^(.+?)\s*[-–—:,]\s*([\d¼-¾⅐-⅞][^,]*)$/;
const TO_TASTE = /^(.+?)\s*[-–—:,]\s*(to taste|as needed|as desired|optional)\s*$/i;

const SERVINGS = /\b(?:serves|servings?|yield|makes)\b\D{0,10}(\d+)|\b(\d+)\s+servings?\b/i;
const HOURS = /(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)\b/i;
const MINUTES = /(\d+)\s*(?:minutes?|mins?|m)\b/i;

// A step we can't stand behind: too short to be an instruction, or trailing
// off. We flag rather than drop — the user is the only one who knows what the
// original said.
function isUnclearStep(text) {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length < 3) return true;
  if (/[.…]{2,}$/.test(text)) return true;
  if (/\b(etc|and so on|as above|see (?:video|link|below))\b/i.test(text)) return true;
  return false;
}

export function parseIngredientLine(line) {
  const text = String(line || '').replace(BULLET, '').trim();
  if (!text) return null;

  const toTaste = text.match(TO_TASTE);
  if (toTaste) return { name: toTaste[1].trim(), quantity: toTaste[2].trim() };

  const leading = text.match(LEADING_AMOUNT);
  if (leading && looksLikeQuantityLine(text)) {
    return { name: leading[2].trim().replace(/^of\s+/i, ''), quantity: leading[1].trim() };
  }

  const trailing = text.match(TRAILING_AMOUNT);
  if (trailing) return { name: trailing[1].trim(), quantity: trailing[2].trim() };

  return { name: text, quantity: '' };
}

function splitSections(lines) {
  const ingredientsAt = lines.findIndex((line) => INGREDIENTS_HEADING.test(line));
  const stepsAt = lines.findIndex((line) => STEPS_HEADING.test(line));

  if (ingredientsAt !== -1 || stepsAt !== -1) {
    const start = ingredientsAt === -1 ? 0 : ingredientsAt + 1;
    const end = stepsAt === -1 ? lines.length : stepsAt;
    return {
      title: ingredientsAt > 0 ? lines[0] : null,
      ingredientLines: lines.slice(start, Math.max(start, end)).filter((l) => !ANY_HEADING.test(l)),
      stepLines: stepsAt === -1 ? [] : lines.slice(stepsAt + 1).filter((l) => !ANY_HEADING.test(l)),
    };
  }

  // No headings: the leading run of amount-shaped lines is the ingredient
  // list, and everything after it is method. A title is only a title if it
  // comes before that run.
  let cursor = 0;
  const title = lines.length && !looksLikeQuantityLine(lines[0]) ? lines[(cursor += 1) - 1] : null;

  const first = cursor;
  while (cursor < lines.length && looksLikeQuantityLine(lines[cursor])) cursor += 1;

  return {
    title,
    ingredientLines: lines.slice(first, cursor),
    stepLines: lines.slice(cursor),
  };
}

function readTotalMinutes(raw) {
  const hours = raw.match(HOURS);
  const minutes = raw.match(MINUTES);
  if (!hours && !minutes) return null;
  return Math.round((hours ? parseFloat(hours[1]) * 60 : 0) + (minutes ? parseInt(minutes[1], 10) : 0));
}

function slugify(text, fallback) {
  const slug = String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return slug || fallback;
}

// Returns the canonical recipe shape, with `servings` and `totalMinutes`
// nullable — the one deviation from the catalog's contract, and a deliberate
// one. The catalog can promise a total time because someone wrote it down.
// A paste can't, and the screen renders the gap rather than a number.
export function parseRecipeText(raw, { origin } = {}) {
  const text = String(raw || '');
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);

  const { title, ingredientLines, stepLines } = splitSections(lines);

  const ingredients = ingredientLines
    .map(parseIngredientLine)
    .filter(Boolean)
    .filter((item) => item.name)
    .map((item) => ({ ...item, quantityMissing: isQuantityMissing(item.quantity) }));

  const steps = stepLines
    .map((line) => line.replace(STEP_NUMBER, '').replace(BULLET, '').trim())
    .filter(Boolean);

  const dishName = title || 'Pasted recipe';
  const servingsMatch = text.match(SERVINGS);

  return {
    id: `pasted-${slugify(dishName, 'recipe')}-${Date.now().toString(36)}`,
    dishName,
    servings: servingsMatch ? parseInt(servingsMatch[1] || servingsMatch[2], 10) : null,
    totalMinutes: readTotalMinutes(text),
    ingredients,
    steps,
    unclearSteps: steps.reduce((acc, step, index) => (isUnclearStep(step) ? [...acc, index] : acc), []),
    origin: origin ?? { kind: 'text', label: 'a recipe you pasted' },
  };
}
